import { useEffect, useMemo } from 'react';
import { FieldValues, useFormContext, UseFormRegister } from 'react-hook-form';

import RadioGroupButton from '../../../components/form/RadioGroupButton';
import { ContentType, SectionTargetExploreEbookBundleType } from '../../../core/graphql/types';
import {
  ISectionTargetType,
  EXPLORE_BUNDLES_TAB_OPTIONS,
  SORT_BY_EXPLORE_OPTIONS,
  SORT_BY_EXPLORE_BUNDLES_BEST_SELLER_OPTIONS,
  SORT_BY_EXPLORE_BUNDLES_OPTIONS,
  PRICE_SETTING_OPTIONS,
  BUNDLES_PRICE_SETTING_OPTIONS,
  RATING_OPTIONS,
  EBOOK_TYPE_OPTIONS,
  EBOOK_FILE_TYPE_OPTIONS,
  CARTOON_TYPE_OPTIONS,
  BUNDLES_CATEGORY_OPTIONS,
  EXPLORE_TYPE_ITEM_OPTIONS,
  SORT_BY_EXPLORE_ITEM_BEST_SELLER_OPTIONS,
  EXPLORE_TYPE_ITEM_VALUE,
} from '../validates/sectionBuilderSchema';

import DropdownSelectNested from './DropdownSelectNested';
import SelectPintoCategoryWrapper from './SelectPintoCategory';

export interface IPropsExploreSectionItemV2 {
  // ─── the “data” fields that get saved into configItems[index] ───
  exploreType?: string;
  sortBy?: string;
  sortType?: string;
  categoryId?: string | number;
  gteRating?: string | number;
  anyPriceTypes?: string[];
  anyFileTypes?: string[];
  anyTypes?: string[];
  anyWritingTypes?: string[];

  // ─── plus the rendering props you still need ────────────────────
  index: number;
  register: UseFormRegister<FieldValues>;
  onDeleteSectionItem?: (idx?: number) => void;
  expanded?: boolean;
}

const ExploreSectionItemV2 = (props: IPropsExploreSectionItemV2) => {
  const { index, register, expanded } = props;

  const { watch, setValue } = useFormContext();

  const [
    rawType, // "explore" | "explore-bundle"
    categoryId,
    exploreType,
    sortBy,
    sortType,
    contentType,
  ] = watch([
    `configItems.${index}.type`,
    `configItems.${index}.categoryId`,
    `configItems.${index}.exploreType`,
    `configItems.${index}.sortBy`,
    `configItems.${index}.sortType`,
    `configItems.${index}.contentType`,
  ]);

  useEffect(() => {
    register(`configItems.${index}.contentType`);
  }, [index, register]);

  const mode: ISectionTargetType = rawType?.includes('bundle') ? ISectionTargetType.EXPLORE_BUNDLES : ISectionTargetType.EXPLORE;

  const exploreTabOptions = useMemo(() => {
    return mode === ISectionTargetType.EXPLORE ? EXPLORE_TYPE_ITEM_OPTIONS : EXPLORE_BUNDLES_TAB_OPTIONS;
  }, [mode]);

  const bestSellerSortOptions = useMemo(() => {
    return mode === ISectionTargetType.EXPLORE ? SORT_BY_EXPLORE_ITEM_BEST_SELLER_OPTIONS : SORT_BY_EXPLORE_BUNDLES_BEST_SELLER_OPTIONS;
  }, [mode]);

  const sortOptions = useMemo(() => {
    return mode === ISectionTargetType.EXPLORE ? SORT_BY_EXPLORE_OPTIONS : SORT_BY_EXPLORE_BUNDLES_OPTIONS;
  }, [mode]);

  const priceOptions = useMemo(() => {
    return mode === ISectionTargetType.EXPLORE ? PRICE_SETTING_OPTIONS : BUNDLES_PRICE_SETTING_OPTIONS;
  }, [mode]);

  // default exploreType
  useEffect(() => {
    if (!exploreType && exploreTabOptions.length) {
      setValue(`configItems.${index}.exploreType`, exploreTabOptions[0].value);
    }
  }, [exploreType, exploreTabOptions, index, setValue]);

  useEffect(() => {
    const prefix = `configItems.${index}.`;

    if (!sortBy && !sortType) {
      if (exploreType === EXPLORE_TYPE_ITEM_VALUE.BestSeller || exploreType === SORT_BY_EXPLORE_BUNDLES_BEST_SELLER_OPTIONS) {
        bestSellerSortOptions[0].value.forEach((opt) => {
          setValue(`${prefix}${opt.keyName}`, opt.value);
        });
      } else if (exploreType === EXPLORE_TYPE_ITEM_VALUE.All) {
        sortOptions[0].value.forEach((opt) => {
          setValue(`${prefix}${opt.keyName}`, opt.value);
        });
      }
    } else if (sortBy && sortType) {
      if (![EXPLORE_TYPE_ITEM_VALUE.All, EXPLORE_TYPE_ITEM_VALUE.BestSeller, SectionTargetExploreEbookBundleType.BestSeller].includes(exploreType)) {
        setValue(`${prefix}sortBy`, null);
        setValue(`${prefix}sortType`, null);
      } else if (
        (exploreType === EXPLORE_TYPE_ITEM_VALUE.BestSeller || exploreType === SORT_BY_EXPLORE_BUNDLES_BEST_SELLER_OPTIONS) &&
        !bestSellerSortOptions.find((opt) => opt.value[0].value === sortBy && opt.value[1].value === sortType)
      ) {
        bestSellerSortOptions[0].value.forEach((opt) => {
          setValue(`${prefix}${opt.keyName}`, opt.value);
        });
      } else if (exploreType === EXPLORE_TYPE_ITEM_VALUE.All && !sortOptions.find((opt) => opt.value[0].value === sortBy && opt.value[1].value === sortType)) {
        sortOptions[0].value.forEach((opt) => {
          setValue(`${prefix}${opt.keyName}`, opt.value);
        });
      }
    }
  }, [exploreType, sortBy, sortType, bestSellerSortOptions, sortOptions, setValue, index]);

  return (
    <div className="flex justify-between space-x-16">
      <div className="w-full collapse">
        <input type="checkbox" className="peer" defaultChecked={expanded} />

        <div className=" collapse-title bg-systemGrays08FillQuaternary text-systemGrays01LabelPrimary peer-checked:bg-systemGrays08FillQuaternary peer-checked:text-systemGrays01LabelPrimary flex justify-between after:content-['+'] peer-checked:after:content-['-'] ease-out duration-300">
          <span>
            <span>Explore Settings</span>
          </span>
        </div>
        <div className="collapse-content text-systemGrays01LabelPrimary peer-checked:bg-systemGrays08FillQuaternary peer-checked:text-systemGrays01LabelPrimary">
          <div className="space-y-8 py-8">
            <RadioGroupButton label="Explore Topic" options={exploreTabOptions} register={register} name={`configItems.${index}.exploreType`} />

            {[EXPLORE_TYPE_ITEM_VALUE.All, EXPLORE_TYPE_ITEM_VALUE.BestSeller, SectionTargetExploreEbookBundleType.BestSeller].includes(exploreType) && (
              <DropdownSelectNested
                label="SortBy"
                options={exploreType === EXPLORE_TYPE_ITEM_VALUE.BestSeller || exploreType === SectionTargetExploreEbookBundleType.BestSeller ? bestSellerSortOptions : sortOptions}
                prefixName={`configItems.${index}`}
                name={[`configItems.${index}.sortBy`, `configItems.${index}.sortType`]}
              />
            )}

            {mode === ISectionTargetType.EXPLORE && (
              <SelectPintoCategoryWrapper name={`configItems.${index}.categoryId`} categoryId={categoryId} contentTypeName={`configItems.${index}.contentType`} />
            )}
            {mode === ISectionTargetType.EXPLORE_BUNDLES && (
              <RadioGroupButton label="Category" options={BUNDLES_CATEGORY_OPTIONS} register={register} name={`configItems.${index}.categoryId`} />
            )}

            {mode === ISectionTargetType.EXPLORE && <RadioGroupButton label="Rating" options={RATING_OPTIONS} register={register} name={`configItems.${index}.gteRating`} />}

            <RadioGroupButton isSetArrayValue label="Price Type" options={priceOptions} register={register} name={`configItems.${index}.anyPriceTypes`} />

            {mode === ISectionTargetType.EXPLORE && (
              <>
                <RadioGroupButton isSetArrayValue label="Ebook Type" options={EBOOK_TYPE_OPTIONS} register={register} name={`configItems.${index}.anyTypes`} />

                <RadioGroupButton isSetArrayValue label="File Type" options={EBOOK_FILE_TYPE_OPTIONS} register={register} name={`configItems.${index}.anyFileTypes`} />

                {contentType === ContentType.Comic && (
                  <RadioGroupButton isSetArrayValue label="Cartoon Type" options={CARTOON_TYPE_OPTIONS} register={register} name={`configItems.${index}.anyWritingTypes`} />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExploreSectionItemV2;
